import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

const STATE_STYLE = {
  needs_attention: "bg-amber-50 text-amber-800 border-amber-200",
  excluded: "bg-slate-50 text-slate-600 border-slate-200",
  deducted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partially_returned: "bg-sky-50 text-sky-700 border-sky-200",
  reversed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  no_stock_effect: "bg-slate-50 text-slate-500 border-slate-200",
};

export function OrderLinesTab() {
  const [attention, setAttention] = useState(true);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/wc/stock/order-lines", { params: { attention, page_size: 50 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], summary: {} }));
  }, [attention]);
  useEffect(() => { load(); }, [load]);
  const s = data?.summary || {};
  const open = Object.entries(s).filter(([k]) => ["unmapped", "variation_unmapped", "ambiguous_mapping", "no_main_location", "no_company", "refund_details_unavailable", "no_activation_baseline", "order_date_unknown", "control_disabled_after_deduction", "movement_failed"].includes(k));

  return (
    <div className="space-y-4" data-testid="wc-order-lines-tab">
      <div className="flex flex-wrap items-center gap-2" data-testid="wc-order-lines-summary">
        {open.length === 0
          ? <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> No order lines need attention</span>
          : open.map(([k, n]) => <span key={k} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800" data-testid={`wc-ol-count-${k}`}><AlertTriangle className="w-3.5 h-3.5" />{data.reasons[k]}: {n}</span>)}
        <button type="button" onClick={() => setAttention((v) => !v)} className="ml-auto h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="wc-ol-toggle">
          {attention ? "Show all order lines" : "Show only lines needing attention"}
        </button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="wc-order-lines">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2">Order</th><th className="px-3 py-2">WC product / variation</th><th className="px-3 py-2">TedOps product</th>
            <th className="px-3 py-2">Qty</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Result</th></tr></thead>
          <tbody>
            {(data?.items || []).map((l) => (
              <tr key={l.line_key} className="border-b border-slate-50" data-testid={`wc-ol-${l.line_key}`}>
                <td className="px-3 py-2 mono text-xs">#{l.wc_order_number || l.wc_order_id}</td>
                <td className="px-3 py-2 mono text-xs">{l.wc_product_id}{l.wc_variation_id ? ` / ${l.wc_variation_id}` : ""}</td>
                <td className="px-3 py-2">{l.product_title || <span className="text-slate-400">-</span>}</td>
                <td className="px-3 py-2">{l.ordered_qty}{l.refunded_qty ? ` (refunded ${l.refunded_qty})` : ""}</td>
                <td className="px-3 py-2 text-xs">{l.order_status}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded-md border px-2 py-0.5 text-xs ${STATE_STYLE[l.state] || ""}`}>{l.reason_text || l.state.replace(/_/g, " ")}</span>
                </td>
              </tr>
            ))}
            {data && !data.items.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-sm">Nothing to show</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
